import { useMemo } from "react";
import DataTable, { type TableColumn } from "react-data-table-component";
import { Eye } from "lucide-react";
import { dataTableCustomStyles } from "../../config/dataTableTheme";
import type { KbGenerationTemplateListItem } from "../../types/gameRuleKbGenerationTemplate";
import { formatDisplayDateTime } from "../../utils/displayDate";
import { createDataTablePagination } from "./DataTablePagination";
import { withScrollableDataTable } from "./withScrollableDataTable";

const pagination = createDataTablePagination({
  rowsPerPageOptions: [10, 20, 30, 50],
});
const ScrollableDataTable = withScrollableDataTable(DataTable);

type GameRuleKbGenerationTemplatesTableProps = {
  readonly rows: readonly KbGenerationTemplateListItem[];
  readonly totalRows: number;
  readonly isLoading: boolean;
  readonly page: number;
  readonly perPage: number;
  readonly onChangePage: (page: number) => void;
  readonly onChangeRowsPerPage: (perPage: number) => void;
  readonly onView: (row: KbGenerationTemplateListItem) => void;
};

export const GameRuleKbGenerationTemplatesTable = ({
  rows,
  totalRows,
  isLoading,
  page,
  perPage,
  onChangePage,
  onChangeRowsPerPage,
  onView,
}: GameRuleKbGenerationTemplatesTableProps) => {
  const columns: TableColumn<KbGenerationTemplateListItem>[] = useMemo(
    () => [
      {
        id: "name",
        name: "Name",
        selector: (row) => row.name,
        sortable: false,
        grow: 2,
        cell: (row) => (
          <button
            type="button"
            onClick={() => onView(row)}
            className="line-clamp-2 text-left text-sm font-medium text-primary-700 hover:underline"
            title={row.name}
          >
            {row.name}
          </button>
        ),
      },
      {
        id: "version",
        name: "Version",
        selector: (row) => row.version,
        sortable: false,
        width: "120px",
        cell: (row) => <span className="text-sm tabular-nums text-gray-700">v{row.version}</span>,
      },
      {
        id: "section_count",
        name: "Section Count",
        selector: (row) => row.sectionCount,
        sortable: false,
        width: "150px",
        cell: (row) => (
          <span className="text-sm tabular-nums text-gray-700">{row.sectionCount}</span>
        ),
      },
      {
        id: "total_versions",
        name: "Total Versions",
        selector: (row) => row.totalVersions,
        sortable: false,
        width: "160px",
        cell: (row) => (
          <span className="text-sm text-gray-700">
            {row.totalVersions} {row.totalVersions === 1 ? "version" : "versions"}
          </span>
        ),
      },
      {
        id: "updated_at",
        name: "Updated At",
        selector: (row) => row.updatedAt,
        sortable: false,
        width: "180px",
        cell: (row) => (
          <span className="text-sm text-gray-700">{formatDisplayDateTime(row.updatedAt)}</span>
        ),
      },
      {
        id: "actions",
        name: "Action",
        sortable: false,
        width: "120px",
        right: true,
        cell: (row) => (
          <div className="flex items-center justify-end">
            <button
              type="button"
              title="View template"
              onClick={() => onView(row)}
              className="inline-flex rounded p-1.5 text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-900"
            >
              <Eye className="h-4 w-4" aria-hidden />
            </button>
          </div>
        ),
      },
    ],
    [onView],
  );

  return (
    <div className="min-h-0 w-full flex-1 overflow-hidden">
      <ScrollableDataTable
        columns={columns}
        data={[...rows]}
        customStyles={dataTableCustomStyles}
        striped
        progressPending={isLoading}
        pagination
        paginationServer
        paginationTotalRows={totalRows}
        paginationPerPage={perPage}
        paginationDefaultPage={page}
        paginationRowsPerPageOptions={[10, 20, 30, 50]}
        paginationComponent={pagination}
        onChangePage={onChangePage}
        onChangeRowsPerPage={(next) => onChangeRowsPerPage(next)}
        keyField="id"
        noDataComponent={
          <div className="p-12 text-center">
            <p className="text-sm font-medium text-gray-700">No game rule templates yet.</p>
            <p className="mt-2 text-sm text-gray-500">
              Create templates from the Word add-in under Create Game Rule.
            </p>
          </div>
        }
        highlightOnHover
        responsive
      />
    </div>
  );
};
